
import type { PropType } from 'vue'
import type { EpbButtonColor, EpbButtonSize, EpbButtonType } from '../epb-button'
import type { Action, Tooltip } from './epb-pill.types'
import { defineComponent } from 'vue'
import { UiTooltip } from '../../keen-ui'
import TransitionSize from '../../transition-size.component.vue'
import { EpbButton } from '../epb-button'

export default defineComponent({
  name: 'EpbPill',
  components: {
    EpbButton,
    TransitionSize,
    UiTooltip,
  },
  props: {
    collapsible: {
      type: Boolean,
    },
    collapsed: {
      type: Boolean,
    },
    type: {
      // line, solid-dark, solid-light
      type: String,
      default: 'line',
    },
    color: {
      type: String,
      default: 'default',
    },
    size: {
      // mini, small, medium, large
      type: String,
      default: 'medium',
    },
    label: {
      type: String,
    },
    icon: {
      type: String,
    },
    iconPosition: {
      // left, right
      type: String,
      default: 'left',
    },
    actions: {
      type: Array as PropType<Array<Action>>,
      default: () => [],
    },
    tooltip: {
      type: [String, Object] as PropType<Tooltip>,
    },
    tooltipPosition: {
      // top, bottom, left, right (all of them can be concatenated with '-start' or '-end')
      type: String,
      default: 'top',
    },
    tooltipOpenDelay: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
  data() {
    return {
      baseClass: 'epb-pill',
      showTooltipTimeout: undefined,
    }
  },
  computed: {
    classes(): string[] {
      const classes = [
        `${this.baseClass}`,
        `${this.baseClass}--${this.type}`,
        `${this.baseClass}--${this.color}`,
        `${this.baseClass}--${this.size}`,
      ]
      if (this.collapsible) {
        classes.push(`${this.baseClass}--collapsible`)
      }
      if (this.collapsed) {
        classes.push(`${this.baseClass}--collapsed`)
      }
      if (this.loading) {
        classes.push(`${this.baseClass}--loading`)
      }
      if (this.disabled) {
        classes.push(`${this.baseClass}--disabled`)
      }
      return classes
    },
    tooltipMessage(): string {
      if (typeof this.tooltip === 'object') {
        return this.tooltip.message
      }
      return this.tooltip
    },
  },
  watch: {
    tooltip: {
      handler(): void {
        this.onTooltipChange()
      },
      deep: true,
    },
  },
  mounted(): void {
    this.onTooltipChange()
  },
  methods: {
    getActionClasses(action: Action): string[] {
      return [
        `${this.baseClass}__button`,
        `${this.baseClass}__button--action`,
        `${this.baseClass}__button--${action.id}`,
        `${this.baseClass}__button--${this.size}`,
      ]
    },
    getActionProps(action: Action): {
      icon: string
      type: EpbButtonType
      color: EpbButtonColor
      size: EpbButtonSize
    } {
      return {
        icon: action.icon,
        type: this.type === 'solid-dark' ? 'cta' : 'void',
        color: 'default',
        size: 'small',
      }
    },
    showTooltip(durationInMillis?: number): void {
      this.$refs.tooltip.tip.show()
      clearTimeout(this.showTooltipTimeout)
      if (durationInMillis) {
        this.showTooltipTimeout = setTimeout(() => {
          this.hideTooltip()
        }, durationInMillis)
      }
    },
    hideTooltip(): void {
      this.$refs.tooltip.tip.hide()
    },
    onTooltipChange(): void {
      const tip = this.$refs.tooltip.tip
      if (typeof this.tooltip === 'object') {
        tip.set({
          boundary: this.tooltip.boundary,
        })
      }
      if (this.tooltipMessage) {
        tip.enable()
      } else {
        tip.hide(0)
        tip.disable()
      }
    },
  },
})
